import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { WorkflowWizardService } from 'app/services/workflow-wizard.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { ModalDirective, MdbCheckboxChange, CheckboxComponent } from 'ng-uikit-pro-standard';
import { StepType } from 'app/models/workflow-step';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { WorkflowTemplateService } from 'app/services/workflow-template.service';
import { WorkflowForm } from 'app/models/workflow-form';
import { Subscription } from 'rxjs';
import { ConnectedAppAuth } from 'app/models/connected-app';
import { WorkflowStepExternalOperation } from 'app/models/workflow-step-external-operation';
import { finalize } from 'rxjs/operators';
import { ConnectedAppAuthorizeComponent } from 'app/views/connected-apps/connected-app-authorize/connected-app-authorize.component';
import { WorkflowFile } from 'app/models/workflow-file';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AcceptancePercentType, WorkflowStepCompletion } from 'app/models/workflow-step-completion';

@Component({
  selector: 'app-custom-step',
  templateUrl: './custom-step.component.html',
  styleUrls: ['./custom-step.component.scss']
})
export class CustomStepComponent implements OnInit, OnDestroy {

  asApproval: boolean = false;
  duplicateSource: string;
  duplicateTarget: string;
  
  acceptancePercentOptions: any = [{ title: 'Responses', value: AcceptancePercentType.ACCEPTANCE_PERCENT_TYPE_RESPONSES },
                                    { title: 'Notified', value: AcceptancePercentType.ACCEPTANCE_PERCENT_TYPE_NOTIFIED }];
                                    
  @ViewChild("duplicateModal", { static: true }) duplicateModal: ModalDirective;
  @ViewChild('deleteExtOpModal') deleteExtOpModal: ModalDirective;
  @ViewChild('addEditExtOpModal') addEditExtOpModal: ModalDirective;
  @ViewChild('authorizeAppExtOp') authorizeAppExtOp: ConnectedAppAuthorizeComponent;

  isAuthorizingExtOp: boolean = false;
  editExtOpIndex: number = -1;
  deleteExtOpIndex: number = -1;
  addEditExtOpFormGroup: FormGroup = null;

  dueAfterDays: number;
  dueAfterHours: number;
  dueAfterExists: boolean;

  timeoutAfterDays: number;
  timeoutAfterHours: number;
  timeoutAfterExists: boolean;

  parentsForms: WorkflowForm[] = [];
  parentsFiles: WorkflowFile[] = [];
  formsChangeSubscription: Subscription = null;

  stepCompletionNamesByPriority: string[] = [];

  constructor(private _validationHandler: ValidationHandler, public workflowWizardService: WorkflowWizardService, private _route: Router,
    private route: ActivatedRoute, private ecoDocsTitleService: EcoDocsTitleService, private workflowTemplateService: WorkflowTemplateService) {

      this.duplicateSource = "";
    this.duplicateTarget = "";
  }

  innerWidth() {
    return window.innerWidth;
  }

  ngOnInit() {
    this.ecoDocsTitleService.setTitle( 'Workflow Wizard > Step Settings', false );
    
    if (this.workflowWizardService.currentId == 'new') {
      if (this.workflowWizardService.currentStepURL != this.workflowWizardService.WIZARD_NEW_STEP) {
        this._route.navigate([this.workflowWizardService.baseURL + this.workflowWizardService.WIZARD_FIRST_STEP]);
      }
    } else {
      if (!this.workflowWizardService.currentStep) {
        if (this.route.snapshot.params.stepid && this.route.snapshot.params.stepid != 'new') {
          // find current step by ID
          this.workflowWizardService.currentStep = this.workflowWizardService.findStepById(+this.route.snapshot.params.stepid);
        }

        if (!this.workflowWizardService.currentStep) {
          this.workflowWizardService.currentStepURL = this.workflowWizardService.WIZARD_STEPS_TREE;
          this._route.navigate([`${this.workflowWizardService.baseURL}${this.workflowWizardService.WIZARD_STEPS_TREE}`]);
          return;
        }
      }
    }

    let dueAfter: number = this.workflowWizardService.currentStep.value.due_after;
    if (dueAfter) {
      this.dueAfterDays = Math.floor(dueAfter / 24.0);
      this.dueAfterHours = dueAfter % 24;
      this.dueAfterExists = true;
    } else {
      this.dueAfterDays = 0;
      this.dueAfterHours = 0;
      this.dueAfterExists = false;
    }

    let timeoutAfter: number = this.workflowWizardService.currentStep.value.timeout_after;
    if (timeoutAfter) {
      this.timeoutAfterDays = Math.floor(timeoutAfter / 24.0);
      this.timeoutAfterHours = timeoutAfter % 24;
      this.timeoutAfterExists = true;
    } else {
      this.timeoutAfterDays = 0;
      this.timeoutAfterHours = 0;
      this.timeoutAfterExists = false;
    }

    this.parentsForms = this.getParentsForms();
    this.parentsFiles = this.getParentsFiles();
    this.stepCompletionNamesByPriority = (<WorkflowStepCompletion[]>this.workflowWizardService.currentStep.get('step_completions').value).sort((a, b) => a.priority - b.priority).flatMap(s => s.name);
    this.formsChangeSubscription = this.workflowWizardService.currentStep.get('step_completions').valueChanges.subscribe((v) => {
      this.parentsForms = this.getParentsForms();
      this.parentsFiles = this.getParentsFiles();
      this.stepCompletionNamesByPriority = (<WorkflowStepCompletion[]>v).sort((a, b) => a.priority - b.priority).flatMap(s => s.name);
    });
  }

  ngOnDestroy(): void {
    if (this.formsChangeSubscription) {
      this.formsChangeSubscription.unsubscribe();
      this.formsChangeSubscription = null;
    }    
  }

  onDueAfterCheckChange(e: MdbCheckboxChange) {
    if (!e.checked)
      this.workflowWizardService.currentStep.get('due_after').setValue(null);
  }

  updateDueAfterValue() {
    this.workflowWizardService.currentStep.get('due_after').setValue(this.dueAfterDays * 24 + this.dueAfterHours);
  }

  onTimeoutAfterCheckChange(e: MdbCheckboxChange) {
    if (!e.checked)
      this.workflowWizardService.currentStep.get('timeout_after').setValue(null);
  }

  updateTimeoutAfterValue() {
    this.workflowWizardService.currentStep.get('timeout_after').setValue(this.timeoutAfterDays * 24 + this.timeoutAfterHours);
  }

  onNextClick() {
    this.workflowWizardService.currentStep.updateValueAndValidity();
    this._validationHandler.onSubmitCheck(this.workflowWizardService.currentStep);
    if (this.workflowWizardService.currentStep.invalid) {
      window.scroll(0,0);
      return;
    }
        
    this.workflowWizardService.currentStepURL = this.workflowWizardService.WIZARD_NEW_STEP_RULES;
    this._route.navigate([`${this.workflowWizardService.baseURL}${this.workflowWizardService.WIZARD_NEW_STEP_RULES}`]);
  }

  back() {
    this.workflowWizardService.currentStep.updateValueAndValidity();
    this._validationHandler.onSubmitCheck(this.workflowWizardService.currentStep);
    if (this.workflowWizardService.currentStep.invalid) {
      window.scroll(0,0);
      return;
    }
    
    if (this.workflowWizardService.workflowType != 'custom') {
      this.workflowWizardService.currentStepURL = this.workflowWizardService.WIZARD_THIRD_STEP;
      this._route.navigate([`${this.workflowWizardService.baseURL}${this.workflowWizardService.WIZARD_THIRD_STEP}`]);
    }
    else {
      this.workflowWizardService.currentStepURL = this.workflowWizardService.WIZARD_STEPS_TREE;
      this._route.navigate([`${this.workflowWizardService.baseURL}${this.workflowWizardService.WIZARD_STEPS_TREE}`]);
    }
  }

  currentStepTypeString() {
    if (this.workflowWizardService.currentStep.controls['workflow_step_type_id'].value == StepType.APPROVAL)
      return 'Approval';
    else if (this.workflowWizardService.currentStep.controls['workflow_step_type_id'].value == StepType.REVIEW)
      return 'Review';
    else if (this.workflowWizardService.currentStep.controls['workflow_step_type_id'].value == StepType.AUTO)
      return 'Auto';
      
    return '';
  }
  
  currentStepTargetsString() {
    if (this.workflowWizardService.currentStep.controls['workflow_step_type_id'].value == StepType.APPROVAL)
      return 'Approver(s)';
    else if (this.workflowWizardService.currentStep.controls['workflow_step_type_id'].value == StepType.REVIEW)
      return 'Reviewer(s)';
    
    return 'Assignee(s)';
  }

  getParentsForms() {
    if (this.workflowWizardService.currentStep.parent == this.workflowWizardService.workflowTemplateFormGroup.get('initial_step_completion')) {
      return this.workflowWizardService.workflowTemplateFormGroup.value.initial_step_completion.forms;
    } else {
      return this.workflowTemplateService.getParentForms(this.workflowWizardService.currentStep.root as FormGroup, (this.workflowWizardService.currentStep.parent as FormGroup).controls['name'].value,
      (this.workflowWizardService.currentStep.parent.parent.parent as FormGroup).controls['name'].value, true);
    }
  }

  getParentsFiles() {
    if (this.workflowWizardService.currentStep.parent == this.workflowWizardService.workflowTemplateFormGroup.get('initial_step_completion')) {
      return this.workflowWizardService.workflowTemplateFormGroup.value.initial_step_completion.files;
    } else {
      return this.workflowTemplateService.getParentFiles(this.workflowWizardService.currentStep.root as FormGroup, (this.workflowWizardService.currentStep.parent as FormGroup).controls['name'].value,
      (this.workflowWizardService.currentStep.parent.parent.parent as FormGroup).controls['name'].value, true);
    }
  }

  addStepExtOp() {
    this.editExtOpIndex = -1;
    this.addEditExtOpFormGroup = this.workflowTemplateService.workflowStepExternalOperationFormGroup();
    this.addEditExtOpModal.show();
  }

  editStepExtOpAtIndex(rowIndex: number) {
    this.editExtOpIndex = rowIndex;
    this.addEditExtOpFormGroup = this.workflowTemplateService.workflowStepExternalOperationFormGroup(this.workflowWizardService.currentStep.controls['workflow_step_external_operations'].value[rowIndex]);
    this.addEditExtOpModal.show();
  }

  onFinishedAddEditExtOp() {
    this._validationHandler.onSubmitCheck(this.addEditExtOpFormGroup);
    if (!this.addEditExtOpFormGroup.valid) {
      return;
    }

    if (this.addEditExtOpFormGroup.value.external_operation_common_settings.auth_methods &&
      this.addEditExtOpFormGroup.value.external_operation_common_settings.auth_methods.length > 0) {
        let connectedAppAuths: ConnectedAppAuth[] = [];
        (<WorkflowStepExternalOperation>this.addEditExtOpFormGroup.value).external_operation_common_settings.auth_methods.forEach((authMethod) => {
          connectedAppAuths.push(authMethod.connected_app_auth);
        });

        this.isAuthorizingExtOp = true;
        this.authorizeAppExtOp.showIfNeedsAuthorization(connectedAppAuths, this.addEditExtOpFormGroup.value.external_operation_common_settings.is_per_user_auth == 0).pipe(finalize(() => this.isAuthorizingExtOp = false)).subscribe((isAllAuthorized) => {
          if (isAllAuthorized) {
            this.onAddEditExtOpAuthorizationComplete();
          }
        });
    } else {
      this.onAddEditExtOpAuthorizationComplete();
    }
  }

  onAddEditExtOpAuthorizationComplete() {
    let currentExtOps: WorkflowStepExternalOperation[] = this.workflowWizardService.currentStep.controls['workflow_step_external_operations'].value || [];
    if (this.editExtOpIndex != -1) {
      currentExtOps[this.editExtOpIndex] = this.addEditExtOpFormGroup.value;
    } else {
      currentExtOps.push(this.addEditExtOpFormGroup.value);
    }
    this.workflowWizardService.currentStep.controls['workflow_step_external_operations'].setValue(currentExtOps);
    this.addEditExtOpModal.hide();
  }

  deleteExtOp() {
    let currentExtOps: WorkflowStepExternalOperation[] = this.workflowWizardService.currentStep.controls['workflow_step_external_operations'].value || [];
    currentExtOps.splice(this.deleteExtOpIndex, 1);
    this.workflowWizardService.currentStep.controls['workflow_step_external_operations'].setValue(currentExtOps);
    this.deleteExtOpModal.hide();    
  }

  dropStepCompletionPriority(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.stepCompletionNamesByPriority, event.previousIndex, event.currentIndex);

    // update the priorities
    (<WorkflowStepCompletion[]>this.workflowWizardService.currentStep.get('step_completions').value).forEach((stepCompletion, index, array) => {
      // priorities are 1 based
      this.workflowWizardService.currentStep.get('step_completions.' + index + '.priority').setValue(this.stepCompletionNamesByPriority.findIndex(v => v == stepCompletion.name) + 1, { emitEvent: false });
    });
  }
}
